import React from 'react'
import { useHistory } from 'react-router-dom'

import {
  ProspectNameAndDobCollectionFormUI,
  ProspectNameAndDobCollectionFormValues,
} from '~/components/screens/ProspectNameAndDobCollectionFormUI'
import { useTenantConfigQuery } from '~/utils/useTenantConfigQuery'
import { secureCache } from '~/utils/secureCache'
import { logEvent } from '~/utils/events'

/**
 * The unauthenticated name step for `health_plan_prospect` signup, reached from `InviteCode` when
 * the BFF reports `collectPreferredName`. Its member twin `AccountNameForm` collects a legal first
 * and last name; a prospect gives only the name they want to be called, which is cached as
 * `preferredName` for `AccountCreation` to post once Auth0 returns.
 *
 * `AuthenticatedProspectNameDobCollection` is the same screen for a prospect who already has a
 * session — same form, but it posts to `create-new-account` itself instead of caching.
 */
const ProspectNameDobCollection: React.FC = () => {
  const { data: tenantData, isLoading: tenantConfigLoading } = useTenantConfigQuery()
  const history = useHistory()

  const tenantConsentForms = tenantData?.consentForms?.filter(
    consentForm => consentForm.needsSignature,
  )

  const initialValues: ProspectNameAndDobCollectionFormValues = {
    preferredName: secureCache.get('preferredName') || '',
    dob: secureCache.get('dob') || '',
    termsAndConditions: false,
    consentForms: [],
  }

  const handleSubmit = async (values: ProspectNameAndDobCollectionFormValues) => {
    const consentForms = values.termsAndConditions ? tenantConsentForms?.map(f => f.id) || [] : []

    logEvent('ACCOUNT_NAME_SCREEN_COMPLETED', {})

    secureCache.set('preferredName', values.preferredName)
    secureCache.set('dob', values.dob)
    secureCache.set('consentForms', JSON.stringify(consentForms))

    // The cache is shared with the member screens and `AccountCreation` posts whatever is in it, so
    // drop the keys a member detour leaves behind: a user who picked a member signup type, got as
    // far as a name or an insurance payer, then came back and chose prospect would otherwise create
    // the prospect with that legal name and payer. Not a blanket `clear()` — `signupType` and
    // `inviteCode` come from the two screens before this one and `create-new-account` rejects a
    // prospect without an invite code. The payer is the backend's to decide either way:
    // `finalize_prospect_signup` derives it from the invite code's coverage product.
    secureCache.remove('firstName')
    secureCache.remove('lastName')
    secureCache.remove('insurancePayerId')

    // Only prospects reach this screen, so there is no member branch to take.
    history.push({ pathname: '/signup/create-account' })
  }

  return (
    <ProspectNameAndDobCollectionFormUI
      initialValues={initialValues}
      onSubmit={handleSubmit}
      isLoading={tenantConfigLoading}
    />
  )
}

export default ProspectNameDobCollection
